'use client';

import { useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { useAnalytics, usePageTracking } from '@/hooks/useAnalytics';
import { useStore } from '@/providers/StoreProvider';
import analyticsModule from '@/lib/analytics';
import { getAnonymousId } from '@/lib/anon-id';
import {
  rehydrateCampaignAttribution,
  stampCampaignAttributionOnUser,
  syncCampaignAttributionFromQueryString,
} from '@/lib/campaign-attribution';
import {
  Events,
  isBuildingDynamicEventName,
  isContactDynamicEventName,
  isGlobalEventName,
  isReviewChoiceEventName,
  isReviewDynamicEventName,
} from '@repo/analytics';
import type { EventName } from '@repo/analytics';

const ALL_EVENT_NAMES = new Set<string>(
  Object.values(Events).flatMap(catalog => Object.keys(catalog as Record<string, unknown>)),
);

function isEventName(name: string): name is EventName {
  return (
    ALL_EVENT_NAMES.has(name) ||
    isGlobalEventName(name) ||
    isBuildingDynamicEventName(name) ||
    isContactDynamicEventName(name) ||
    isReviewDynamicEventName(name) ||
    isReviewChoiceEventName(name)
  );
}

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

export function AnalyticsEffects({ children }: AnalyticsProviderProps) {
  const lastIdentifiedRef = useRef<string | null>(null);

  const searchParams = useSearchParams();

  // Defer analytics initialization until after window load; queue events until then.
  useEffect(() => {
    analyticsModule.scheduleAnalyticsLoadAfterWindowLoad();
  }, []);

  // Re-apply persisted (cookie-backed) campaign attribution on load. This makes
  // it survive reloads, new tabs, and return visits without the UTMs in the URL.
  // Runs before the URL capture + usePageTracking so first-touch is present and
  // the annotation is queued ahead of the first pageview.
  useEffect(() => {
    rehydrateCampaignAttribution();
  }, []);

  // Capture UTM / campaign params from the URL (landing + client navigations).
  // Persists first-touch (write-once) and last-touch (30-day sliding window),
  // then annotates every event. Only applies when params are present, so it
  // never wipes an earlier campaign annotation.
  useEffect(() => {
    const search = searchParams?.toString();
    if (search) syncCampaignAttributionFromQueryString(search);
  }, [searchParams]);

  // Automatically track page views
  usePageTracking();

  const analytics = useAnalytics();
  const profile = useStore(s => s.profile?.profile);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (typeof window === 'undefined') return;
      if (!(e.target instanceof Element)) return;
      // Image-navigation controls (carousel arrows/dots) drive quickview events,
      // not card taps. Never let their clicks bubble into a card/listing tap even
      // if they sit inside a `data-analytics-event` card.
      if (e.target.closest('[data-analytics-skip="true"]')) return;
      // Bubble-phase listener ensures that inner controls can opt out via stopPropagation()
      const el = e.target.closest<HTMLElement>('[data-analytics-event][data-analytics-props]');
      if (!el) return;

      const eventName = el.dataset.analyticsEvent;
      const rawProps = el.dataset.analyticsProps;
      if (!eventName || !rawProps) return;
      if (!isEventName(eventName)) return;
      // For now we only support the typed card click event (avoid accidental tracking spam)
      let props: unknown = undefined;
      try {
        props = JSON.parse(rawProps);
      } catch {
        return;
      }
      // Route through the analytics hook so Datadog mirroring stays consistent.
      analytics.track(eventName, props as any);
    };

    document.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('click', onClick);
    };
  }, []);

  // Identify user once when profile becomes available.
  useEffect(() => {
    const userId = (profile as any)?.uid ?? (profile as any)?.id ?? null;
    // uid preferred; fallback to profile id
    const email = (profile as any)?.email as string | undefined;
    const firstName = (profile as any)?.firstName as string | undefined;
    const lastName = (profile as any)?.lastName as string | undefined;
    const fullNameFromParts = [firstName, lastName].filter(Boolean).join(' ') || undefined;
    const name =
      ((profile as any)?.name as string | undefined) ||
      ((profile as any)?.fullName as string | undefined) ||
      fullNameFromParts ||
      undefined;

    if (!userId) {
      const anonId = getAnonymousId();
      const identityKey = `anon::${anonId ?? ''}`;
      if (lastIdentifiedRef.current === identityKey) return;
      lastIdentifiedRef.current = identityKey;
      return;
    }

    const anonId = getAnonymousId() || undefined;

    // Profile can hydrate in phases (id/email first, name later). Only skip if nothing changed.
    const identityKey = `${userId}::${email ?? ''}::${name ?? ''}::${anonId ?? ''}`;
    if (lastIdentifiedRef.current === identityKey) return;
    lastIdentifiedRef.current = identityKey;
    // Also attach the anonymous id so pre-login activity can be correlated without "identity duplication" confusion.
    analytics.setUser(userId, email, name, anonId);
    // Stamp acquisition (first + last touch) onto the user profile at login.
    stampCampaignAttributionOnUser(String(userId));
  }, [analytics, profile]);

  return <>{children}</>;
}
